import { ErrorBoundary } from '@sentry/react'
import { debounce, forEach, isEqual, map } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import feathers from 'utility/client'
import { DeviceState } from './DeviceState'
import { useDispatch } from 'react-redux'
import { StateActions } from 'store/modules/state/actions'
import { ContractTypeLabel } from 'utility/utility'
import { useStatistics } from 'modules/business/hooks/useStatistics'

const Fallback = () => {
  return <div className="d-flex justify-content-center align-items-center m-4 p-5 border rounded">
    <div className="opacity-50 text-center">
      <h5 className="m-0 p-0">
        <i className="fa fa-exclamation-triangle mr-2"></i> We are experiencing an issue!
        <p className="m-0 p-0 mt-2"><small>The problem has been identified and reported already. Team is on it to make statistics available shortly.</small></p>
      </h5>
    </div>
  </div>
}

export const Business = ({ business, onExpand = () => {}, active }) => {
  const [visible, setVisible] = useState(false)
  const [devices, setDevices] = useState([])
  const dispatch = useDispatch()

  const stateBuffer = useRef(new Map())

  const onUpdate = useCallback(
    debounce((data) => {
      dispatch(StateActions.put({device: data.value.device, state: data.value}))
      stateBuffer.current.set(data.key, data.value)
      const nextState = Array.from(stateBuffer.current.values())

      const nextDevices = map(nextState, 'device').sort()
      if (!isEqual(devices, nextDevices)) {
        setDevices(nextDevices)
      } else {
        console.log('Skipping device list update')
      }
    }, 200),
  [])

  const onDelete = useCallback((data) => {
    dispatch(StateActions.remove(data.value.device))
    stateBuffer.current.delete(data.value.device)
    const nextDevices = map(Array.from(stateBuffer.current.values()), 'device').sort()
    setDevices(nextDevices)
  }, [dispatch])

  const onSubscribe = (response) => {
    forEach(response.data, (s) => {
      stateBuffer.current.set(s.device, s)
    })

    const nextState = Array.from(stateBuffer.current.values())

    const nextDevices = map(nextState, 'device').sort()
    if (!isEqual(devices, nextDevices)) {
      setDevices(nextDevices)
    } else {
      console.log('Skipping device list update')
    }
  }

  const { subscribe, unsubscribe } = useStatistics(
    business._id,
    onUpdate,
    onDelete,
    onSubscribe,
    false
  )

  useEffect(() => {
    if (visible) {
      feathers.io.emit('devices', 'statistic',{ business: business._id }, {}, (e, response) => {
        if (e) console.log(e)

        setDevices(response)
      })

      stateBuffer.current.clear()
      setDevices([])

      subscribe()
    } else {
      unsubscribe()
    }
  }, [visible])

  useEffect(() => {
    if (!active && visible) {
      setVisible(false)
    }

    if (active && !visible) {
      setVisible(true)
    }
  }, [active, visible])

  const toggle = () => {
    if (!visible) {
      onExpand()
    }
    setVisible(!visible)
  }

  return <div className="card">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-center" onClick={toggle}>
        <div>
          {business.name} <br />
          <small>{ ContractTypeLabel?.[business.contractType] || 'Unspecified' }</small>
        </div>
        <div className="ml-3">
          <i className={`fa ${visible ? 'fa-chevron-up': 'fa-chevron-down'}`}></i>
        </div>
      </div>
      <div style={{ display: visible ? 'block' : 'none' }}>
        <hr className="my-2"/>

        <ErrorBoundary fallback={<Fallback />}>
          <div className="row">
            {
              map(devices, (device, index) => (
                <div key={index} className="col-md-6 mb-4">
                  <DeviceState
                    data={{ label: device.deviceName, device: device.device }}
                  />
                </div>
              ))
            }
          </div>
        </ErrorBoundary>
      </div>
    </div>
  </div>
}
